import { z } from 'zod';

// Prospecção na base pública de CNPJ (Receita Federal). Busca + importação para a carteira.

export const PORTES_PROSPECCAO = ['ME', 'EPP', 'DEMAIS'] as const;
export type PorteProspeccao = (typeof PORTES_PROSPECCAO)[number];
/** Porte no cnpj-service: MICRO · EPP · DEMAIS · NAO_INFORMADO */
export const PORTE_SERVICO: Record<PorteProspeccao, readonly string[]> = { ME: ['MICRO'], EPP: ['EPP'], DEMAIS: ['DEMAIS', 'NAO_INFORMADO'] };
export const porteLabel = (porte: string | null | undefined) =>
  porte === 'MICRO' ? 'ME' : porte === 'EPP' ? 'EPP' : porte === 'DEMAIS' ? 'Demais' : null;

/** abertura = mais recentes primeiro · cnpj = ordem do cadastro (mais rápida) */
export const ORDENS_PROSPECCAO = ['abertura', 'cnpj'] as const;

const dataISO = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (AAAA-MM-DD).');
const cnpjBase = z.string().regex(/^[0-9A-Z]{12}[0-9]{2}$/, 'CNPJ inválido.');

const buscaBase = z
  .object({
    /** Opcional: sem CNAE, a busca precisa de nome, de município ou de período de abertura curto (evita varrer um estado inteiro). */
    cnaes: z.array(z.string().regex(/^\d{7}$/, 'CNAE inválido.')).max(20).default([]),
    /** Trecho da razão social ou do nome fantasia (≥ 3 letras). Exige município. */
    nome: z.string().trim().max(80).optional().transform((v) => v || undefined),
    incluirSecundarios: z.boolean().default(false),
    /** Uma ou mais UFs (multiseleção). Repetidas são descartadas. */
    ufs: z
      .array(z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'UF inválida.'))
      .min(1, 'Escolha ao menos uma UF.')
      .max(27)
      .transform((l) => [...new Set(l)]),
    municipios: z.array(z.number().int().positive()).max(50).default([]),
    bairroOuCep: z.string().trim().max(80).optional(),
    aberturaDe: dataISO.optional(),
    aberturaAte: dataISO.optional(),
    /** Faixa de capital social (R$), opcional: refina a busca, não substitui CNAE/município. */
    capitalDe: z.number().min(0).max(1e15).optional(),
    capitalAte: z.number().min(0).max(1e15).optional(),
    portes: z.array(z.enum(PORTES_PROSPECCAO)).max(3).default([]),
    tipos: z.array(z.enum(['MATRIZ', 'FILIAL'])).max(2).default([]),
    somenteCelular: z.boolean().default(false),
    somenteEmail: z.boolean().default(false),
    excluirMei: z.boolean().default(false),
    ocultarCarteira: z.boolean().default(true),
    ordem: z.enum(ORDENS_PROSPECCAO).default('abertura'),
    /** proximoCursor da página anterior; ausente = primeira página (traz também as contagens) */
    cursor: z.string().max(40).optional(),
    limit: z.number().int().min(10).max(100).default(50),
  })
  .strict();
const datasEmOrdem = (d: { aberturaDe?: string; aberturaAte?: string }) => !d.aberturaDe || !d.aberturaAte || d.aberturaDe <= d.aberturaAte;
const erroDatas = { message: 'A data inicial é depois da final.', path: ['aberturaAte'] };
const capitalEmOrdem = (d: { capitalDe?: number; capitalAte?: number }) => d.capitalDe === undefined || d.capitalAte === undefined || d.capitalDe <= d.capitalAte;
const erroCapital = { message: 'O capital social mínimo é maior que o máximo.', path: ['capitalAte'] };

type Regras = { cnaes: string[]; nome?: string; municipios: number[]; aberturaDe?: string; aberturaAte?: string };
/** Sem CNAE, nome e município: só UF + abertura, com período de no máximo estes dias (recém-abertas do estado). */
export const DIAS_MAX_ABERTURA_SEM_CNAE = 92;
const diasDoPeriodo = (de: string, ate?: string) =>
  Math.round(((ate ? Date.parse(`${ate}T00:00:00Z`) : Date.now()) - Date.parse(`${de}T00:00:00Z`)) / 86_400_000);
const periodoCurto = (d: Regras) => !!d.aberturaDe && diasDoPeriodo(d.aberturaDe, d.aberturaAte) <= DIAS_MAX_ABERTURA_SEM_CNAE;
const nomeCurto = (d: Regras) => !d.nome || d.nome.length >= 3;
const nomeComMunicipio = (d: Regras) => !d.nome || d.municipios.length > 0;
const temCriterio = (d: Regras) => d.cnaes.length > 0 || !!d.nome || d.municipios.length > 0 || periodoCurto(d);
const erroNomeCurto = { message: 'Digite ao menos 3 letras do nome.', path: ['nome'] };
const erroNomeSemMunicipio = { message: 'Para buscar pelo nome, escolha ao menos um município.', path: ['municipios'] };
const erroSemCriterio = {
  message: 'Sem atividade (CNAE), escolha um município, busque pelo nome ou use um período de abertura de até 90 dias.',
  path: ['municipios'],
};

export const prospeccaoBuscaSchema = buscaBase
  .refine(datasEmOrdem, erroDatas)
  .refine(capitalEmOrdem, erroCapital)
  .refine(temCriterio, erroSemCriterio)
  .refine(nomeCurto, erroNomeCurto)
  .refine(nomeComMunicipio, erroNomeSemMunicipio);
export type ProspeccaoBusca = z.input<typeof prospeccaoBuscaSchema>;
/** Filtros da busca sem paginação — usados em "importar todas do filtro". */
export const prospeccaoFiltrosSchema = buscaBase
  .omit({ cursor: true, limit: true })
  .refine(datasEmOrdem, erroDatas)
  .refine(capitalEmOrdem, erroCapital)
  .refine(temCriterio, erroSemCriterio)
  .refine(nomeCurto, erroNomeCurto)
  .refine(nomeComMunicipio, erroNomeSemMunicipio);
export type ProspeccaoFiltros = z.input<typeof prospeccaoFiltrosSchema>;

/** Máximo por importação (seleção manual ou "todas do filtro"); o plano pode limitar antes. */
export const MAX_IMPORTACAO_PROSPECCAO = 5000;

export const prospeccaoImportarSchema = z
  .object({
    /** empresas marcadas uma a uma… */
    cnpjs: z.array(cnpjBase).min(1, 'Selecione ao menos uma empresa.').max(MAX_IMPORTACAO_PROSPECCAO).optional(),
    /** …ou todas as que a busca encontra */
    filtros: prospeccaoFiltrosSchema.optional(),
    distribuicao: z.enum(['EU', 'RODIZIO']).default('EU'),
    vendedorIds: z.array(z.string().uuid()).max(50).default([]),
    /** Também atendem todas as empresas importadas, além do principal (specs/22). Até 4; só quem atribui carteira. */
    vendedoresAdicionais: z.array(z.string().uuid()).max(4, 'No máximo 4 vendedores além do principal.').default([]),
    tagIds: z.array(z.string().uuid()).max(20).default([]),
  })
  .strict()
  .refine((d) => !!d.cnpjs !== !!d.filtros, { message: 'Envie as empresas selecionadas ou os filtros da busca.' });

export const MOTIVOS_IGNORADO = {
  NAO_ENCONTRADA: 'Não está mais ativa na base',
  JA_NA_CARTEIRA: 'Já está na carteira',
  NAO_CONTACTAR: 'Marcado como NÃO CONTACTAR',
  LIMITE_PLANO: 'Passou do limite de clientes do plano',
} as const;

/** Importadas SEM WhatsApp (o vendedor completa depois na ficha): por quê. */
export const MOTIVOS_SEM_TELEFONE = {
  SEM_TELEFONE: 'Sem telefone na Receita',
  TELEFONE_INVALIDO: 'Telefone da Receita inválido',
  WHATSAPP_JA_CADASTRADO: 'Telefone já usado por outro cliente',
  WHATSAPP_REPETIDO: 'Mesmo telefone de outra empresa importada',
} as const;
export type MotivoSemTelefone = keyof typeof MOTIVOS_SEM_TELEFONE;
export type MotivoIgnorado = keyof typeof MOTIVOS_IGNORADO;

const MINUSCULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'na', 'no', 'a', 'o']);
const SIGLAS = new Set(['ME', 'EPP', 'EIRELI', 'S/A', 'SA', 'S.A.', 'LTDA.', 'CIA', 'MEI', 'II', 'III', 'IV', 'SP', 'PR', 'RJ', 'MG', 'SC', 'RS']);

/** "DROGARIA SAO JOAO LTDA" → "Drogaria Sao Joao Ltda" (a Receita grava tudo em maiúsculas). */
export function capitalizar(s: string | null | undefined): string | null {
  if (!s) return null;
  return s
    .trim()
    .split(/\s+/)
    .map((p, i) => {
      if (SIGLAS.has(p) && p !== 'LTDA.') return p;
      const l = p.toLocaleLowerCase('pt-BR');
      if (i > 0 && MINUSCULAS.has(l)) return l;
      return l.replace(/(^|[-/(])(\p{L})/gu, (_m, a: string, b: string) => a + b.toLocaleUpperCase('pt-BR'));
    })
    .join(' ');
}

/** Remove acentos (a base da Receita é gravada sem acento em endereço/bairro). */
export const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
